import React, { useState, useEffect, useCallback, useRef } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { FaLocationArrow } from "react-icons/fa";
import { MdError } from "react-icons/md";
import "./TMLocationMap.scss";
import { useGetService } from "../../hooks";
import { API } from "../../constants";
import {
  getSimulatedUserLocations,
  userLocationMockApi,
} from "./user-location.mock";
import TMLocationMapHeader from "./components/TMLocationHeader/TMLocationHeader";
import { TMListSideBar } from "./components/TMListSidebar/TMListSidebar";
import AdminLocationManager, {
  AdminLocation,
} from "./components/AdminLocationManager/AdminLocationManager";
import {
  calculateDistance,
  createAdvancedMapMarker,
  formatDistance,
  getMarkerColorByTime,
  getTimeStatusLabel,
} from "./TMLocationMap.service";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";

export interface UserLocation {
  id: string;
  latitude?: number;
  longitude?: number;
  updatedOn?: string;
  distance?: number;
}

export interface IBusLocation {
  id: string;
  latitude?: number;
  longitude?: number;
  updatedOn?: string;
  distance?: number;
  users?: { id: string; name: string }[];
}

export interface IGreeterLocation {
  id: string;
  latitude?: number;
  longitude?: number;
  updatedOn?: string;
  distance?: number;
  name?: string;
  email?: string;
  phone?: string;
  vendorType?: string;
}

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const defaultCenter = {
  lat: 28.4638137,
  lng: -81.3114611,
};

const LIBRARIES: ("marker" | "geometry" | "routes")[] = [
  "marker",
  "geometry",
  "routes",
];

const busLocations: IBusLocation[] = [
  {
    id: "101",
    latitude: 25.8082574,
    longitude: -80.2823083,
    updatedOn: "2025-10-27T11:30:00.000Z",
  },
  {
    id: "102",
    latitude: 25.784329,
    longitude: -80.320903,
    updatedOn: "2025-10-27T11:25:00.000Z",
  },
  {
    id: "103",
    latitude: 25.807627,
    longitude: -80.288369,
    updatedOn: "2025-10-27T11:20:00.000Z",
    users: [{ id: "497901", name: "ISABEL RAMALLO" }],
  },
];

const TMLocationsMap: React.FC = () => {
  const [userLocations, setUserLocations] = useState<UserLocation[]>([]);
  const [greeterLocations, setGreeterLocations] = useState<IGreeterLocation[]>(
    []
  );
  const [unavailableUsers, setUnavailableUsers] = useState<UserLocation[]>([]);
  const [adminLocation, setAdminLocation] = useState<AdminLocation | null>(
    null
  );

  const location = useLocation();
  //   const listedTms = location.state?.listedTms;
  //   const shipName = location.state?.shipName;

  const [listedTms, setListedTms] = useState<
    { id: string; movementDate: string }[]
  >([]);

  const params = new URLSearchParams(location.search);
  const assignmentDate = params.get("assignmentDate");
  const shipName = params.get("shipName");

  const movementDate = moment().format(assignmentDate || "YYYY-MM-DD");

  const {
    response,
    isLoading,
    error: apiError,
    refetch,
  } = useGetService(
    `${API.USER_LOCATION.LIST_TMS_LOC}?movementDate=${movementDate}&ship=${shipName}&select=latitude,longitude,id,updatedOn`
  );

  const greeterLocs = useGetService(`${API.USER_LOCATION.GREETER_LOCATIONS}`);

  //   const [response, setResponse] = useState(userLocationMockApi);

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [selectedMarker, setSelectedMarker] = useState<string | null>(null);
  const [markerMap, setMarkerMap] = useState<
    Map<string, google.maps.marker.AdvancedMarkerElement>
  >(new Map());
  const [infoWindow, setInfoWindow] = useState<google.maps.InfoWindow | null>(
    null
  );

  const [filteredUsers, setFilteredUsers] = useState<UserLocation[]>([]);
  const [highlightedUser, setHighlightedUser] = useState<string | null>(null);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showLocationManager, setShowLocationManager] = useState(false);
  const [lastUpdatedOn, setLastUpdatedOn] = useState(new Date().toISOString());

  // Track if map has been manually moved by user
  const hasUserInteractedRef = useRef(false);
  const isInitialLoadRef = useRef(true);

  const markerLibraryRef = useRef<google.maps.MarkerLibrary | null>(null);
  const adminMarkerRef =
    useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const isLibraryLoadedRef = useRef(false);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: LIBRARIES,
    version: "weekly",
  });

  console.log({ apiError, greeterFetchError: greeterLocs.error });

  const navigate = useNavigate();

  // Load admin location from localStorage on mount
  useEffect(() => {
    const listedTmsStorage =
      JSON.parse(sessionStorage.getItem("listedTms") || "[]") || [];
    if (!listedTmsStorage || !Array.isArray(listedTmsStorage) || !shipName) {
      navigate("/dashboard");
      return;
    }
    setListedTms(listedTmsStorage as any);

    const savedLocation = localStorage.getItem("adminLocation");
    if (savedLocation) {
      try {
        const parsed = JSON.parse(savedLocation);
        setAdminLocation(parsed);
      } catch (error) {
        console.error("Failed to parse saved location:", error);
      }
    }
  }, [shipName]);

  // Auto-update simulation
  useEffect(() => {
    const id = setInterval(() => {
      setLastUpdatedOn(new Date().toISOString());
      //   setResponse(getSimulatedUserLocations());
      refetch(
        `${API.USER_LOCATION.LIST_TMS_LOC}?movementDate=${movementDate}&ship=${shipName}&select=latitude,longitude,id,updatedOn`,
        true
      );
      greeterLocs.refetch(`${API.USER_LOCATION.GREETER_LOCATIONS}`, true);
    }, 10000);

    return () => {
      clearInterval(id);
    };
  }, [shipName, movementDate]);

  // Process user locations with distance calculations
  useEffect(() => {
    if (response?.data) {
      const validLocations = response.data
        .filter(
          (
            loc: any
          ): loc is UserLocation & { latitude: number; longitude: number } =>
            loc.latitude !== undefined &&
            loc.longitude !== undefined &&
            !isNaN(loc.latitude) &&
            !isNaN(loc.longitude)
        )
        .map((loc: UserLocation & { latitude: number; longitude: number }) => {
          if (adminLocation) {
            const distance = calculateDistance(
              loc.latitude,
              loc.longitude,
              adminLocation.lat,
              adminLocation.lng
            );

            return {
              ...loc,
              distance,
            };
          }
          return loc;
        });

      const invalidLocations = response.data.filter(
        (loc: any) =>
          loc.latitude === undefined ||
          loc.longitude === undefined ||
          isNaN(loc.latitude) ||
          isNaN(loc.longitude)
      );

      console.log("Valid locations:", validLocations);
      console.log("Count:", validLocations.length);
      console.log("Invalid locations:", invalidLocations);
      console.log("Invalid count:", invalidLocations.length);

      setUserLocations(validLocations);
      setFilteredUsers(validLocations);
    }
  }, [response, adminLocation]);

  useEffect(() => {
    if (greeterLocs?.response?.data) {
      const validLocations = greeterLocs?.response?.data
        .filter(
          (
            loc: any
          ): loc is UserLocation & { latitude: number; longitude: number } =>
            loc.latitude !== undefined &&
            loc.longitude !== undefined &&
            !isNaN(loc.latitude) &&
            !isNaN(loc.longitude)
        )
        .map((loc: UserLocation & { latitude: number; longitude: number }) => {
          if (adminLocation) {
            const distance = calculateDistance(
              loc.latitude,
              loc.longitude,
              adminLocation.lat,
              adminLocation.lng
            );

            return {
              ...loc,
              distance,
            };
          }
          return loc;
        });

      const invalidLocations = greeterLocs?.response.data.filter(
        (loc: any) =>
          loc.latitude === undefined ||
          loc.longitude === undefined ||
          isNaN(loc.latitude) ||
          isNaN(loc.longitude)
      );

      console.log("Valid locations:", validLocations);
      console.log("Count:", validLocations.length);
      console.log("Invalid locations:", invalidLocations);
      console.log("Invalid count:", invalidLocations.length);

      setGreeterLocations(validLocations);
    }
  }, [greeterLocs.response, adminLocation]);

  // Initialize marker library
  useEffect(() => {
    if (!map || !isLoaded || isLibraryLoadedRef.current) {
      return;
    }

    const initializeLibrary = async () => {
      try {
        console.log("=== LOADING MARKER LIBRARY (ONE TIME) ===");

        const markerLibrary = (await google.maps.importLibrary(
          "marker"
        )) as google.maps.MarkerLibrary;
        markerLibraryRef.current = markerLibrary;

        const infoWin = new google.maps.InfoWindow();
        infoWindowRef.current = infoWin;
        setInfoWindow(infoWin);

        isLibraryLoadedRef.current = true;
        console.log("Marker library loaded successfully ✓");
      } catch (err) {
        console.error("Marker library loading error:", err);
      }
    };

    initializeLibrary();
  }, [map, isLoaded]);

  // Create/update admin marker
  useEffect(() => {
    if (!map || !isLoaded || !markerLibraryRef.current || !adminLocation) {
      return;
    }

    if (adminMarkerRef.current) {
      console.log("Updating admin marker position");
      adminMarkerRef.current.position = {
        lat: adminLocation.lat,
        lng: adminLocation.lng,
      };
      return;
    }

    const createAdminMarker = async () => {
      try {
        console.log("=== CREATING ADMIN MARKER ===");
        const { PinElement, AdvancedMarkerElement } = markerLibraryRef.current!;
        const infoWin = infoWindowRef.current;

        const pin = new PinElement({
          background: "#4F46E5",
          borderColor: "#FFFFFF",
          glyphColor: "#FFFFFF",
          scale: 2.0,
        });

        const marker = new AdvancedMarkerElement({
          map,
          position: { lat: adminLocation.lat, lng: adminLocation.lng },
          content: pin.element,
          title: "Admin Location",
          zIndex: 9999,
        });

        marker.addListener("click", () => {
          infoWin?.setContent(`
            <div style="padding: 8px; font-family: system-ui;">
              <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: 600;">
                📍 Admin Location
              </h3>
              <p style="margin: 0; font-size: 12px; color: #666;">
                ${adminLocation.lat.toFixed(6)}, ${adminLocation.lng.toFixed(6)}
              </p>
              <p style="margin: 4px 0 0 0; font-size: 11px; color: #9ca3af;">
                Source: ${
                  adminLocation.source === "browser" ? "Browser" : "Manual"
                }
              </p>
            </div>
          `);
          infoWin?.open(map, marker);
        });

        adminMarkerRef.current = marker;
        console.log("Admin marker created ✓");
      } catch (err) {
        console.error("Admin marker creation error:", err);
      }
    };

    createAdminMarker();
  }, [map, isLoaded, adminLocation]);

  // Function to fetch travel time from Google
  const fetchGoogleTravelTime = async (
    userLat: number,
    userLng: number,
    adminLat: number,
    adminLng: number
  ): Promise<{ duration: string; distance: string } | null> => {
    try {
      const directionsService = new google.maps.DirectionsService();

      const result = await directionsService.route({
        origin: { lat: userLat, lng: userLng },
        destination: { lat: adminLat, lng: adminLng },
        travelMode: google.maps.TravelMode.DRIVING,
      });

      if (result.routes && result.routes.length > 0) {
        const route = result.routes[0];
        if (route.legs && route.legs.length > 0) {
          const leg = route.legs[0];
          return {
            duration: leg.duration?.text || "N/A",
            distance: leg.distance?.text || "N/A",
          };
        }
      }
      return null;
    } catch (error) {
      console.error("Error fetching travel time from Google:", error);
      return null;
    }
  };

  // Create/update user markers
  useEffect(() => {
    if (!map || !isLoaded || !markerLibraryRef.current) {
      return;
    }

    console.log("=== CREATING/UPDATING USER MARKERS ===");
    const { PinElement, AdvancedMarkerElement } = markerLibraryRef.current;
    const infoWin = infoWindowRef.current;
    const newMarkerMap = new Map(markerMap);

    // markerMap.forEach((marker, id) => {
    //   if (!userLocations.find((u) => u.id === id)) {
    //     marker.map = null;
    //     newMarkerMap.delete(id);
    //   }
    // });

    userLocations?.forEach((user) => {
      if (!user.latitude || !user.longitude) return;

      const markerColor = getMarkerColorByTime(user.updatedOn);
      const existingMarker = newMarkerMap.get(user.id);

      const pin = new PinElement({
        background: markerColor,
        borderColor: "#FFFFFF",
        glyphColor: "#FFFFFF",
        scale: 1.2,
      });

      if (existingMarker) {
        existingMarker.position = {
          lat: user.latitude,
          lng: user.longitude,
        };
        existingMarker.content = pin.element;
      } else {
        const marker = new AdvancedMarkerElement({
          map,
          position: { lat: user.latitude, lng: user.longitude },
          content: pin.element,
          title: user.id,
        });

        marker.addListener("click", () => {
          setSelectedMarker(user.id);
          setHighlightedUser(user.id);

          const directionsLink =
            adminLocation && adminLocation.lat
              ? `
            <a
              href="https://www.google.com/maps/dir/?api=1&origin=${user.latitude},${user.longitude}&destination=${adminLocation.lat},${adminLocation.lng}&travelmode=driving"
              target="_blank"
              rel="noopener noreferrer"
              style="
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 6px;
                padding: 7px 12px;
                background: white;
                color: #4285f4;
                text-decoration: none;
                border: 1.5px solid #4285f4;
                border-radius: 8px;
                font-size: 12px;
                font-weight: 600;
                transition: all 0.2s ease;
                margin-top: 4px;
              "
              onmouseover="this.style.background='#f0f7ff'; this.style.borderColor='#357ae8'"
              onmouseout="this.style.background='white'; this.style.borderColor='#4285f4'"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              <span>Get Directions</span>
            </a>
          `
              : "";

          const travelTimeButton =
            adminLocation && adminLocation.lat
              ? `
            <button
              id="fetch-travel-time-${user.id}"
              style="
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 6px;
                padding: 7px 12px;
                background: #10b981;
                color: white;
                border: none;
                border-radius: 8px;
                font-size: 12px;
                font-weight: 600;
                cursor: pointer;
                transition: all 0.2s ease;
                margin-top: 8px;
                width: 100%;
              "
              onmouseover="this.style.background='#059669'"
              onmouseout="this.style.background='#10b981'"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span>Get Accurate Travel Time</span>
            </button>
            <div id="google-travel-time-${user.id}" style="margin-top: 8px; display: none;"></div>
          `
              : "";

          const distanceInfo =
            user.distance !== undefined
              ? `
              <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb;">
                <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
                  <span style="font-size: 14px;">📏</span>
                  <span style="font-size: 12px; color: #374151;">
                    <strong>Distance:</strong> ${formatDistance(user.distance)}
                  </span>
                </div>
                ${directionsLink}
                ${travelTimeButton}
              </div>
            `
              : "";

          infoWin?.setContent(`
            <div style="padding: 8px; font-family: system-ui; min-width: 200px;">
              <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: 600;">
                👤 ${user.id}
              </h3>
              <div style="display: flex; align-items: center; gap: 4px; margin-bottom: 4px;">
                <div style="width: 8px; height: 8px; border-radius: 50%; background: ${markerColor};"></div>
                <span style="font-size: 11px; color: #6b7280;">
                  ${getTimeStatusLabel(user.updatedOn)}
                </span>
              </div>
              <p style="margin: 4px 0; font-size: 12px; color: #666;">
                ${user.latitude?.toFixed(6)}, ${user.longitude?.toFixed(6)}
              </p>
              ${
                user.updatedOn
                  ? `<p style="margin: 4px 0 0 0; font-size: 11px; color: #9ca3af;">
                  Updated: ${new Date(user.updatedOn).toLocaleString()}
                </p>`
                  : ""
              }
              ${distanceInfo}
            </div>
          `);
          infoWin?.open(map, marker);

          // Add event listener for travel time button after InfoWindow is rendered
          if (adminLocation && adminLocation.lat) {
            setTimeout(() => {
              const travelButton = document.getElementById(
                `fetch-travel-time-${user.id}`
              );
              const travelTimeDiv = document.getElementById(
                `google-travel-time-${user.id}`
              );

              if (travelButton && travelTimeDiv) {
                travelButton.onclick = async () => {
                  // Show loading state
                  travelButton.innerHTML = `
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="animation: spin 1s linear infinite;">
                      <style>
                        @keyframes spin {
                          from { transform: rotate(0deg); }
                          to { transform: rotate(360deg); }
                        }
                      </style>
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M12 6v6l4 2"></path>
                    </svg>
                    <span>Fetching...</span>
                  `;
                  travelButton.style.pointerEvents = "none";

                  const result = await fetchGoogleTravelTime(
                    user.latitude!,
                    user.longitude!,
                    adminLocation.lat,
                    adminLocation.lng
                  );

                  //   <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
                  //           <span style="font-size: 14px;">📏</span>
                  //           <span style="font-size: 12px; color: #166534;">
                  //             <strong>Distance:</strong> ${result.distance}
                  //           </span>
                  //         </div>
                  if (result) {
                    travelTimeDiv.style.display = "block";
                    travelTimeDiv.innerHTML = `
                      <div style="padding: 8px; background: #f0fdf4; border: 1px solid #86efac; border-radius: 6px;">
                        <div style="font-size: 11px; font-weight: 700; color: #16a34a; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.05em;">
                          🗺️ Google Maps Route
                        </div>
                        
                        <div style="display: flex; align-items: center; gap: 8px;">
                          <span style="font-size: 14px;">⏱️</span>
                          <span style="font-size: 12px; color: #166534;">
                            <strong>Travel Time:</strong> ${result.duration}
                          </span>
                        </div>
                      </div>
                    `;
                    travelButton.style.display = "none";
                  } else {
                    travelButton.innerHTML = `
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                      <span>Failed - Try Again</span>
                    `;
                    travelButton.style.background = "#ef4444";
                    travelButton.style.pointerEvents = "auto";
                  }
                };
              }
            }, 100);
          }
        });

        newMarkerMap.set(user.id, marker);
      }
    });

    greeterLocations?.forEach((user) => {
      if (!user.latitude || !user.longitude) return;

      const markerColor = getMarkerColorByTime(user.updatedOn);
      const existingMarker = newMarkerMap.get(user.id);

      //   const pin = new PinElement({
      //     background: markerColor,
      //     borderColor: "#FFFFFF",
      //     glyphColor: "#FFFFFF",
      //     scale: 3.4,
      //   });

      const markerContent = createAdvancedMapMarker(
        markerColor,
        42,
        "Greeter",
        true
      );

      if (existingMarker) {
        existingMarker.position = {
          lat: user.latitude,
          lng: user.longitude,
        };
        existingMarker.content = markerContent;
      } else {
        const marker = new AdvancedMarkerElement({
          map,
          position: { lat: user.latitude, lng: user.longitude },
          content: markerContent,
          title: user.name || user.email,
        });

        marker.addListener("click", () => {
          setSelectedMarker(user.id);
          setHighlightedUser(user.id);

          const directionsLink =
            adminLocation && adminLocation.lat
              ? `
              <a
                href="https://www.google.com/maps/dir/?api=1&origin=${user.latitude},${user.longitude}&destination=${adminLocation.lat},${adminLocation.lng}&travelmode=driving"
                target="_blank"
                rel="noopener noreferrer"
                style="
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  gap: 6px;
                  padding: 7px 12px;
                  background: white;
                  color: #4285f4;
                  text-decoration: none;
                  border: 1.5px solid #4285f4;
                  border-radius: 8px;
                  font-size: 12px;
                  font-weight: 600;
                  transition: all 0.2s ease;
                  margin-top: 4px;
                "
                onmouseover="this.style.background='#f0f7ff'; this.style.borderColor='#357ae8'"
                onmouseout="this.style.background='white'; this.style.borderColor='#4285f4'"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <span>Get Directions</span>
              </a>
            `
              : "";

          const travelTimeButton =
            adminLocation && adminLocation.lat
              ? `
              <button
                id="fetch-travel-time-${user.id}"
                style="
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  gap: 6px;
                  padding: 7px 12px;
                  background: #10b981;
                  color: white;
                  border: none;
                  border-radius: 8px;
                  font-size: 12px;
                  font-weight: 600;
                  cursor: pointer;
                  transition: all 0.2s ease;
                  margin-top: 8px;
                  width: 100%;
                "
                onmouseover="this.style.background='#059669'"
                onmouseout="this.style.background='#10b981'"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <span>Get Accurate Travel Time</span>
              </button>
              <div id="google-travel-time-${user.id}" style="margin-top: 8px; display: none;"></div>
            `
              : "";

          const distanceInfo =
            user.distance !== undefined
              ? `
                <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb;">
                  <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
                    <span style="font-size: 14px;">📏</span>
                    <span style="font-size: 12px; color: #374151;">
                      <strong>Distance:</strong> ${formatDistance(
                        user.distance
                      )}
                    </span>
                  </div>
                  ${directionsLink}
                  ${travelTimeButton}
                </div>
              `
              : "";

          const userPhone = user.phone
            ? `<p style="margin: 4px 0; font-size: 12px; color: #666;">
                  ${user.phone}
                </p>`
            : "";
          const vendorType = user.vendorType
            ? `<p style="margin: 4px 0; font-size: 12px; color: #666;">
                  ${user.vendorType}
                </p>`
            : "";
          infoWin?.setContent(`
              <div style="padding: 8px; font-family: system-ui; min-width: 200px;">
                <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: 600;">
                  👤 ${user.name || user.email}
                </h3>
                <div style="display: flex; align-items: center; gap: 4px; margin-bottom: 4px;">
                  <div style="width: 8px; height: 8px; border-radius: 50%; background: ${markerColor};"></div>
                  <span style="font-size: 11px; color: #6b7280;">
                    ${getTimeStatusLabel(user.updatedOn)}
                  </span>
                </div>
                ${userPhone}
                ${vendorType}
                ${
                  user.updatedOn
                    ? `<p style="margin: 4px 0 0 0; font-size: 11px; color: #9ca3af;">
                    Updated: ${new Date(user.updatedOn).toLocaleString()}
                  </p>`
                    : ""
                }
                ${distanceInfo}
              </div>
            `);
          infoWin?.open(map, marker);

          // Add event listener for travel time button after InfoWindow is rendered
          if (adminLocation && adminLocation.lat) {
            setTimeout(() => {
              const travelButton = document.getElementById(
                `fetch-travel-time-${user.id}`
              );
              const travelTimeDiv = document.getElementById(
                `google-travel-time-${user.id}`
              );

              if (travelButton && travelTimeDiv) {
                travelButton.onclick = async () => {
                  // Show loading state
                  travelButton.innerHTML = `
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="animation: spin 1s linear infinite;">
                        <style>
                          @keyframes spin {
                            from { transform: rotate(0deg); }
                            to { transform: rotate(360deg); }
                          }
                        </style>
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 6v6l4 2"></path>
                      </svg>
                      <span>Fetching...</span>
                    `;
                  travelButton.style.pointerEvents = "none";

                  const result = await fetchGoogleTravelTime(
                    user.latitude!,
                    user.longitude!,
                    adminLocation.lat,
                    adminLocation.lng
                  );

                  if (result) {
                    travelTimeDiv.style.display = "block";
                    travelTimeDiv.innerHTML = `
                        <div style="padding: 8px; background: #f0fdf4; border: 1px solid #86efac; border-radius: 6px;">
                          <div style="font-size: 11px; font-weight: 700; color: #16a34a; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.05em;">
                            🗺️ Google Maps Route
                          </div>
                          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
                            <span style="font-size: 14px;">📏</span>
                            <span style="font-size: 12px; color: #166534;">
                              <strong>Distance:</strong> ${result.distance}
                            </span>
                          </div>
                          <div style="display: flex; align-items: center; gap: 8px;">
                            <span style="font-size: 14px;">⏱️</span>
                            <span style="font-size: 12px; color: #166534;">
                              <strong>Travel Time:</strong> ${result.duration}
                            </span>
                          </div>
                        </div>
                      `;
                    travelButton.style.display = "none";
                  } else {
                    travelButton.innerHTML = `
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="8" x2="12" y2="12"></line>
                          <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        <span>Failed - Try Again</span>
                      `;
                    travelButton.style.background = "#ef4444";
                    travelButton.style.pointerEvents = "auto";
                  }
                };
              }
            }, 100);
          }
        });

        newMarkerMap.set(user.id, marker);
      }
    });

    // TODO: Remove after demo
    busLocations?.forEach((bus) => {
      if (!bus.latitude || !bus.longitude) return;

      const existingMarker = newMarkerMap.get(bus.id);

      const busIcon = document.createElement("div");
      busIcon.style.width = "34px";
      busIcon.style.height = "34px";
      busIcon.style.position = "relative";
      busIcon.style.cursor = "pointer";
      busIcon.innerHTML = `
        <svg width="34px" height="34px" viewBox="0 0 1024 1024" class="icon" version="1.1" xmlns="http://www.w3.org/2000/svg" style="display: block;">
          <path d="M341.333333 768a21.333333 21.333333 0 0 0-21.333333-21.333333h-85.333333a21.333333 21.333333 0 0 0-21.333334 21.333333v128a21.333333 21.333333 0 0 0 21.333334 21.333333h85.333333a21.333333 21.333333 0 0 0 21.333333-21.333333v-128zM810.666667 768a21.333333 21.333333 0 0 0-21.333334-21.333333h-85.333333a21.333333 21.333333 0 0 0-21.333333 21.333333v128a21.333333 21.333333 0 0 0 21.333333 21.333333h85.333333a21.333333 21.333333 0 0 0 21.333334-21.333333v-128z" fill="#455A64" />
          <path d="M800 85.333333h-576A53.333333 53.333333 0 0 0 170.666667 138.666667V789.333333s0 42.666667 42.666666 42.666667h597.333334c42.666667 0 42.666667-42.666667 42.666666-42.666667V138.666667A53.333333 53.333333 0 0 0 800 85.333333z" fill="${
            bus.users ? "red" : "red"
          }" />
          <path d="M213.333333 479.978667a32 32 0 0 1-32 32h-21.333333a32 32 0 0 1-32-32V416A32 32 0 0 1 160 384h21.333333a32 32 0 0 1 32 32v63.978667zM896 479.978667a32 32 0 0 1-32 32h-21.333333a32 32 0 0 1-32-32V416a32 32 0 0 1 32-32h21.333333a32 32 0 0 1 32 32v63.978667z" fill="${
            bus.users ? "red" : "red"
          }" />
          <path d="M288 693.333333m-53.333333 0a53.333333 53.333333 0 1 0 106.666666 0 53.333333 53.333333 0 1 0-106.666666 0Z" fill="#FFF8E1" />
          <path d="M736 693.333333m-53.333333 0a53.333333 53.333333 0 1 0 106.666666 0 53.333333 53.333333 0 1 0-106.666666 0Z" fill="#FFF8E1" />
          <path d="M778.666667 128h-533.333334A32 32 0 0 0 213.333333 160v341.333333a53.333333 53.333333 0 0 0 53.333334 53.333334h490.666666a53.333333 53.333333 0 0 0 53.333334-53.333334v-341.333333A32 32 0 0 0 778.666667 128z" fill="#9FA8DA" />
          <path d="M810.666667 256H213.333333V160A32 32 0 0 1 245.333333 128h533.333334A32 32 0 0 1 810.666667 160V256z" fill="#263238" />
          <path d="M256 170.666667h234.666667v42.666666H256zM533.333333 170.666667h85.333334v42.666666h-85.333334z" fill="#64DD17" />
          <path d="M448 554.666667h-170.666667v-160a32 32 0 0 1 32-32h106.666667a32 32 0 0 1 32 32V554.666667zM746.666667 554.666667h-170.666667v-160a32 32 0 0 1 32-32h106.666667a32 32 0 0 1 32 32V554.666667z" fill="#7986CB" />
          <path d="M606.506667 554.666667c-5.674667-6.336-9.173333-13.589333-9.173334-21.333334 0-23.125333 29.312-42.666667 64-42.666666s64 19.541333 64 42.666666c0 7.744-3.498667 14.997333-9.173333 21.333334h48.042667c2.24-6.848 3.797333-13.909333 3.797333-21.333334 0-47.061333-47.850667-85.333333-106.666667-85.333333s-106.666667 38.272-106.666666 85.333333c0 7.424 1.557333 14.485333 3.797333 21.333334h48.042667z" fill="#3949AB" />
        </svg>
      `;

      if (existingMarker) {
        existingMarker.position = { lat: bus.latitude, lng: bus.longitude };
        existingMarker.content = busIcon;
      } else {
        const marker = new AdvancedMarkerElement({
          map,
          position: { lat: bus.latitude, lng: bus.longitude },
          content: busIcon,
          title: `Bus ${bus.id}`,
          zIndex: 100,
          gmpClickable: true,
        });

        marker.addListener("click", () => {
          const users = bus.users || [];
          const hasUsers = users.length > 0;

          const headerGradient =
            "linear-gradient(135deg, #0f5499 0%, #0f5499 100%)";
          const avatarGradient =
            "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)";

          const userListHTML = hasUsers
            ? `
            <div style="max-height: 180px; overflow-y: auto; padding: 0 4px; scrollbar-width: thin;">
              ${users
                .map(
                  (u) => `
                <div style="
                  display: flex; 
                  align-items: center; 
                  gap: 12px; 
                  padding: 10px 8px; 
                  margin-bottom: 6px;
                  background: #fff; 
                  border-radius: 8px;
                  transition: background 0.2s;
                  border: 1px solid #f3f4f6;
                "
                onmouseover="this.style.background='#f9fafb'; this.style.borderColor='#e5e7eb'"
                onmouseout="this.style.background='#fff'; this.style.borderColor='#f3f4f6'"
                >
                   <div style="
                      width: 32px; 
                      height: 32px; 
                      background: ${avatarGradient}; 
                      color: #4f46e5; 
                      border-radius: 50%; 
                      display: flex; 
                      align-items: center; 
                      justify-content: center; 
                      font-size: 11px; 
                      font-weight: 800;
                      box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                      flex-shrink: 0;
                   ">
                      ${u.name.charAt(0).toUpperCase()}
                   </div>
                   
                   <div style="display: flex; flex-direction: column; width: 100%;">
                     <span style="font-size: 13px; font-weight: 600; color: #111827; line-height: 1.2;">
                       ${u.name}
                     </span>
                     <span style="font-size: 10px; color: #9ca3af; font-weight: 500; letter-spacing: 0.3px;">
                       ID: ${u.id}
                     </span>
                   </div>
                </div>
              `
                )
                .join("")}
            </div>
          `
            : `
            <div style="
              padding: 24px 0; 
              text-align: center; 
              background: #f9fafb; 
              border-radius: 12px;
              border: 1px dashed #e5e7eb;
              margin-top: 4px;
            ">
              <div style="font-size: 28px; margin-bottom: 8px; filter: grayscale(0.2);">🚍</div>
              <div style="font-size: 13px; font-weight: 600; color: #374151;">Bus is Empty</div>
              <span style="font-size: 11px; color: #9ca3af;">No TM's currently on board</span>
            </div>
          `;

          infoWin?.setContent(`
            <div style="
              font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
              width: 260px;
              overflow: hidden;
            ">
              
              <div style="
                background: ${headerGradient};
                padding: 16px;
                border-radius: 12px;
                color: white;
                box-shadow: 0 4px 6px -1px rgba(99, 102, 241, 0.2);
                margin-bottom: 12px;
                position: relative;
                margin-right: 12px;
              ">
                <div style="display: flex; justify-content: space-between; align-items: flex-start;">
                  <div>
                    <div style="
                      font-size: 10px; 
                      text-transform: uppercase; 
                      letter-spacing: 1px; 
                      opacity: 0.9; 
                      font-weight: 600;
                      margin-bottom: 2px;
                    ">
                      Team members
                    </div>
                    <h3 style="margin: 0; font-size: 18px; font-weight: 700; display: flex; align-items: center; gap: 6px;">
                      Bus 
                      <span style="font-size: 12px; background: rgba(255,255,255,0.2); padding: 2px 6px; border-radius: 6px; font-weight: 500;">
                         #${bus.id}
                      </span>
                    </h3>
                  </div>
                  
                  <div style="
                    background: white; 
                    color: #6366f1; 
                    padding: 4px 8px; 
                    border-radius: 20px; 
                    font-size: 11px; 
                    font-weight: 700;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                  ">
                    ${hasUsers ? `${users.length} ON BOARD` : "ACTIVE"}
                  </div>
                </div>
        
                <div style="margin-top: 12px; display: flex; align-items: center; gap: 4px; font-size: 11px; opacity: 0.9;">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                  <span>${bus.latitude?.toFixed(5)}, ${bus.longitude?.toFixed(
            5
          )}</span>
                </div>
              </div>
        
              ${
                hasUsers
                  ? `<div style="
                      font-size: 11px; 
                      font-weight: 700; 
                      color: #6b7280; 
                      text-transform: uppercase; 
                      letter-spacing: 0.5px;
                      margin: 0 0 8px 4px;
                    ">
                    Passenger Manifest
                   </div>`
                  : ""
              }
        
              ${userListHTML}
        
            </div>
          `);

          infoWin?.open(map, marker);
        });

        newMarkerMap.set(bus.id, marker);
      }
    });

    setMarkerMap(newMarkerMap);
    console.log(`Total user markers: ${newMarkerMap.size}`);
  }, [userLocations, greeterLocations, map, isLoaded, adminLocation]);

  useEffect(() => {
    const unavailableList = listedTms?.map((tm) => {
      const locExist = userLocations.some((loc) => loc.id === tm.id);
      if (locExist) {
        return null;
      }
      return { id: tm.id, movementDate: tm.movementDate };
    });

    setUnavailableUsers(
      unavailableList
        ? (unavailableList as any)?.filter(
            (x: any) => !!x && movementDate === x.movementDate
          ) || []
        : []
    );
  }, [userLocations, listedTms, movementDate]);

  const onLoad = useCallback((map: google.maps.Map) => {
    console.log("Map loaded ✓");
    setMap(map);

    const trafficLayer = new google.maps.TrafficLayer();
    trafficLayer.setMap(map);

    // Track user interactions with the map
    map.addListener("dragstart", () => {
      hasUserInteractedRef.current = true;
    });

    map.addListener("zoom_changed", () => {
      hasUserInteractedRef.current = true;
    });
  }, []);

  const onUnmount = useCallback(() => {
    console.log("Map unmounting, cleaning up...");
    setMap(null);

    markerMap.forEach((marker) => {
      marker.map = null;
    });
    setMarkerMap(new Map());

    if (adminMarkerRef.current) {
      adminMarkerRef.current.map = null;
      adminMarkerRef.current = null;
    }

    infoWindow?.close();
    setInfoWindow(null);
    infoWindowRef.current = null;

    isLibraryLoadedRef.current = false;
    markerLibraryRef.current = null;
  }, [markerMap, infoWindow]);

  const handleUserClick = useCallback(
    (user: UserLocation) => {
      if (!map || !user.latitude || !user.longitude) return;

      const marker = markerMap.get(user.id);
      if (marker) {
        map.setCenter({ lat: user.latitude, lng: user.longitude });
        map.setZoom(15);
        setHighlightedUser(user.id);
        google.maps.event.trigger(marker, "click");

        // Reset user interaction flag since this is programmatic
        hasUserInteractedRef.current = false;
      }
    },
    [map, markerMap]
  );

  const handleFocusAdmin = () => {
    if (!map || !adminLocation) return;
    map.setCenter({ lat: adminLocation.lat, lng: adminLocation.lng });
    map.setZoom(12);
    setHighlightedUser(null);

    // Reset user interaction flag since this is programmatic
    hasUserInteractedRef.current = false;
  };

  const handleRefresh = () => {
    console.log("Refreshing locations...");
    // setResponse(getSimulatedUserLocations());
    refetch(
      `${API.USER_LOCATION.LIST_TMS_LOC}?movementDate=${movementDate}&ship=${shipName}&select=latitude,longitude,id,updatedOn`
    );
    greeterLocs.refetch(`${API.USER_LOCATION.GREETER_LOCATIONS}`);
    setLastUpdatedOn(new Date().toISOString());
  };

  const handleLocationChange = (location: AdminLocation | null) => {
    setAdminLocation(location);
    if (location) {
      localStorage.setItem("adminLocation", JSON.stringify(location));

      // Only center map if this is initial load or user hasn't interacted
      if (map && (isInitialLoadRef.current || !hasUserInteractedRef.current)) {
        map.setCenter({ lat: location.lat, lng: location.lng });
        map.setZoom(12);
      }

      isInitialLoadRef.current = false;
    } else {
      localStorage.removeItem("adminLocation");
    }
  };

  if (loadError) {
    return (
      <div className="map-error-container">
        <div className="error-card">
          <div className="error-content">
            <MdError className="error-icon" />
            <div>
              <p className="error-title">Failed to load Google Maps</p>
              <p className="error-message">Check API key and connection</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isLoaded || isLoading || greeterLocs?.isLoading) {
    return (
      <div className="map-loading-container">
        <div className="loading-content">
          <div className="spinner"></div>
          <p className="loading-text">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="location-map-wrapper">
      <TMLocationMapHeader
        totalArrivals={userLocations.length}
        lastUpdated={lastUpdatedOn}
        unavailableUsers={unavailableUsers}
        hasAdminLocation={!!adminLocation}
        onFocusAdmin={handleFocusAdmin}
        onRefresh={handleRefresh}
        onToggleLocationManager={() =>
          setShowLocationManager(!showLocationManager)
        }
        isLoading={isLoading || greeterLocs?.isLoading}
        showLocationManager={showLocationManager}
      />

      {showLocationManager && (
        <div className="location-manager-panel">
          <AdminLocationManager
            onLocationChange={handleLocationChange}
            initialLocation={adminLocation}
            onClose={() => setShowLocationManager(false)}
          />
        </div>
      )}

      <div className="map-main-content">
        <TMListSideBar
          isSidebarCollapsed={isSidebarCollapsed}
          setHighlightedUser={setHighlightedUser}
          handleUserClick={handleUserClick}
          userLocations={[...userLocations, ...greeterLocations]}
          setIsSidebarCollapsed={setIsSidebarCollapsed}
          currentLocation={
            adminLocation
              ? { lat: adminLocation.lat, lng: adminLocation.lng }
              : null
          }
          highlightedUser={highlightedUser}
          key={"sidebar"}
        />

        <div className="map-container">
          {(isLoading || greeterLocs?.isLoading) && (
            <div className="loading-overlay">
              <div className="loading-content">
                <div className="spinner"></div>
                <p className="loading-text">Loading locations...</p>
              </div>
            </div>
          )}

          {(apiError || greeterLocs?.error) && (
            <div className="alert-container">
              <div className="alert alert--error">
                <MdError className="alert-icon" />
                <div className="alert-content">
                  <p className="alert-title">Error</p>
                  <p className="alert-message">
                    {(apiError || greeterLocs?.error)?.message ||
                      "Failed to load team member locations"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {!adminLocation && (
            <div className="alert-container">
              <div className="alert alert--warning">
                <FaLocationArrow className="alert-icon" />
                <div className="alert-content">
                  <p className="alert-title">Admin Location Not Set</p>
                  <p className="alert-message">
                    Click "Set Admin Location" to enable distance calculations
                  </p>
                </div>
              </div>
            </div>
          )}

          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            // center={
            //   adminLocation && isInitialLoadRef.current
            //     ? { lat: adminLocation.lat, lng: adminLocation.lng }
            //     : defaultCenter
            // }
            center={adminLocation || defaultCenter}
            zoom={adminLocation && isInitialLoadRef.current ? 12 : 6}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={{
              mapTypeControl: true,
              streetViewControl: false,
              fullscreenControl: true,
              mapId: "DEMO_MAP_ID",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default TMLocationsMap;
